import { Public, Subject } from '../types';
import {
  DatabaseKey,
  DatabaseRequest,
  ExtendedDatabaseVersionChangeEvent
} from './types';

export type IDatabaseService<TStores extends Record<string, unknown>> = (
  Public<DatabaseService<TStores>>
);

/** Props used to create a @see IDBObjectStore. */
export type DatabaseStoreProps<TItem> = {
  /** Whether the primary key should auto-increment. */
  autoIncrement?: boolean;
  /** Get a unique key for the item in this @see IDBObjectStore. */
  key: keyof(TItem) & string;
  /** Indexes used to quickly look for @see TItem when not using their @see key. */
  indexes?: Record<string, {
    key: keyof(TItem) & string;
  }>;
};

/** Props used to create a @see IDBDatabase. */
export type DatabaseProps<TStores extends Record<string, unknown>> = {
  /** Name of the @see IDBDatabase. */
  name: string;
  /** Props for each @see IDBObjectStore in the @see IDBDatabase. */
  stores: {
    [key in keyof(TStores)]: DatabaseStoreProps<TStores[key]>;
  };
  /**
   * Version of the @see IDBDatabase.
   *
   * @note Bumping this causes DatabaseService._upgradeDatabase to be called creating new stores as needed.
   */
  version: number;
};

/** Wrapper for the @see IDBFactory API to increase type security & use of promises. */
export class DatabaseService<TStores extends Record<string, unknown>> {
  readonly databaseChange = new Subject<void>();
  private _database: Promise<IDBDatabase> | null = null;
  private _props: DatabaseProps<TStores>;

  constructor(props: DatabaseProps<TStores>) {
    this._props = props;
  }

  /** Get all items within a store. */
  async getAll<KStore extends keyof(TStores)>(
    storeName: KStore
  ): Promise<TStores[KStore][]> {
    const store = await this._startTransaction(storeName);
    return this._requestToPromise(store.getAll());
  }

  /** Get an item within a store. */
  async getByIndex<KStore extends keyof(TStores)>(
    storeName: KStore,
    indexName: string,
    value: DatabaseKey
  ): Promise<TStores[KStore] | undefined> {
    const [item] = await this._getByIndexWithStore(
      storeName,
      indexName,
      value
    );
    return item;
  }

  async removeByIndex<KStore extends keyof(TStores)>(
    storeName: KStore,
    indexName: string,
    value: DatabaseKey
  ): Promise<void> {
    const [item, store] = await this._getByIndexWithStore(
      storeName,
      indexName,
      value
    );
    const { key } = this._props.stores[storeName];
    if (!item) {
      return;
    }
    await this._requestToPromise(store.delete(item[key] as string));
    this.databaseChange.next();
  }

  /** Update an item within a store. */
  async update<KStore extends keyof(TStores)>(
    storeName: KStore,
    item: TStores[KStore]
  ): Promise<TStores[KStore]> {
    const store = await this._startTransaction(storeName);
    await this._requestToPromise(store.put(item));
    this.databaseChange.next();
    return item;
  }

  /** Get the full database. */
  protected async _getDatabase(): Promise<IDBDatabase> {
    if (!this._database) {
      const { name, version } = this._props;
      const databaseRequest = window.indexedDB.open(name, version);
      databaseRequest.onupgradeneeded = (event) => {
        const extendedEvent = event as ExtendedDatabaseVersionChangeEvent;
        extendedEvent.database = (
          event.target as DatabaseRequest<IDBDatabase>
        ).result;
        this._upgradeDatabase(extendedEvent);
      };
      // Intentionally not awaited so _database will be set for subsequent calls.
      this._database = this._requestToPromise(databaseRequest);
    }
    return this._database;
  }

  /** Get an item within a store. */
  protected async _getByIndexWithStore<KStore extends keyof(TStores)>(
    storeName: KStore,
    indexName: string,
    value: DatabaseKey
  ): Promise<[TStores[KStore] | undefined, IDBObjectStore]> {
    const store = await this._startTransaction(storeName);
    const index = store.index(indexName);
    const request = index.get(value) as IDBRequest<TStores[KStore]>;
    return [await this._requestToPromise(request), store];
  }

  /** Convert a database request into a promise. */
  protected _requestToPromise<TResult>(
    request: DatabaseRequest<TResult>
  ): Promise<TResult> {
    return new Promise((resolve, reject) => {
      request.onerror = (event) => {
        reject(event);
      };
      request.onsuccess = (event) => {
        resolve((event.target as DatabaseRequest<TResult>).result);
      };
    });
  }

  /** Start a transaction within the object store. */
  protected async _startTransaction(storeName: keyof(TStores)): Promise<IDBObjectStore> {
    const database = await this._getDatabase();
    const transaction = database.transaction(storeName as string, 'readwrite');
    return transaction.objectStore(storeName as string);
  }

  /** Upgrade the database from one version to the next. */
  protected _upgradeDatabase({
    database
  }: ExtendedDatabaseVersionChangeEvent): void {
    const { stores } = this._props;
    Object.entries(stores).forEach(([storeName, storeOptions]) => {
      if (database.objectStoreNames.contains(storeName)) {
        // You can only create new object stores and not query/update existing ones with the current indexDB spec.
        return;
      }

      const {
        autoIncrement,
        key,
        indexes
      } = storeOptions as DatabaseStoreProps<TStores[keyof(TStores)]>;
      const objectStore = database.createObjectStore(storeName, { autoIncrement, keyPath: key });
      Object.entries(indexes ?? {}).forEach(([indexName, { key }]) => {
        if (!objectStore.indexNames.contains(indexName)) {
          objectStore.createIndex(indexName, key);
        }
      });
    });
  }
}
